import React, { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Button,
  Tooltip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import Card, { LoaderWrapper } from "../../../components/Cards/Card";
import NativeObject from "../../../components/NativeObject/NativeObject";
import LayoutWrapper from "../../../components/LayoutWrapper/LayoutWrapper";
import ComboHeader from "../../../components/ComboHeader/ComboHeader";
import Kpi from "../../../components/KPI/Kpi";
import utils from "../../../QlikUtils";
import ObjectLoader from "../../../components/ObjectLoader/ObjectLoader";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CircleLoader from "../../../components/Loaders/CircleLoader";
import ProgressBar from "../../../components/ProgressBar/ProgressBar";
import Bar from "../../../components/Bar/Bar";
import InsightsGoldenDesktop from "./InsightsGolden";

function SellOutGoldenStoresDesktop({ app, config }) {
  const notFullHd = useMediaQuery({ query: "(max-width: 1536px)" });
  const configData = config?.sellOutGoldenStores ?? null;
  const [openReports, setOpenReports] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [buttonGroup, setButtonGroup] = useState([
    { value: "Resumo", selected: true },
    { value: "Detalhe", selected: false },
  ]);
  const [openScreenGroup, setOpenScreenGroup] = useState([
    { value: "Overview", selected: true },
    { value: "Relatórios", selected: false },
    { value: "Insights", selected: false },
  ]);
  const [showIngsights, setIngsights] = useState(false);
  const [textQuarter, setTextQuarter] = useState();

  const initializeRows = async (id, card) => {
    await utils.createHyperCubeByQlikIdToTable(app, id, 35, async (res) => {
      card.rows = await utils
        .extractDataSet(res)
        .slice(1)
        .map((item) => {
          // convert names to title case
          const name = item[0].value.split(" ");
          for (var i = 0; i < name.length; i++) {
            name[i] =
              name[i].charAt(0).toUpperCase() + name[i].slice(1).toLowerCase();
          }
          return {
            name: name.join(" "),
            scoreValue: parseInt(item[1].value),
            goalValue: parseInt(item[2].value.slice(2, -3).replace(/\./g, "")),
            value: parseInt(item[3].value.slice(2, -3).replace(/\./g, "")),
          };
        });
      const a = card.rows
        .map((e) => Math.max(e.value, e.goalValue))
        .reduce(function (prev, current) {
          return Math.max(prev, current);
        });
      card.highestValue = a;
      const roundedUpScale =
        Math.round(a / 10 ** (a.toString().length - 1)) *
        10 ** (a.toString().length - 1);
      card.scale = [0, roundedUpScale / 2, roundedUpScale];
      setTimeout(() => {
        setLoading(false);
      }, 1500);
    });
  };

  const exportToXLS = async (objectId, isContainer) => {
    setLoadingExcel(true);
    let id = objectId;

    if (isContainer) {
      await app.visualization
        .get(objectId)
        .then((vis) => {
          id = vis.model.items.activeId;
        })
        .catch(() => setLoadingExcel(false));
    }

    if (app && id) {
      await app.visualization
        .get(id)
        .then((vis) => {
          vis.table.exportData({ format: "OOXML", state: "A" }).then((link) => {
            setLoadingExcel(false);
            const xlsLink = `https://qsense.mtrix.com.br${
              process.env.REACT_APP_PREFIX
            }${link.substring(link.indexOf("tempcontent"))}`;
            window.open(xlsLink);
          });
        })
        .catch(() => setLoadingExcel(false));
    }
  };

  const getColorFromPercentage = (percentage) => {
    switch (true) {
      case percentage < 85:
        return "#E95248";
      case percentage < 100:
        return "#FDB949";
      default:
        return "#38BC9A";
    }
  };

  for (const card of configData.cards) {
    card.value = Kpi({
      app,
      objectId: card.nativeObject.valueId,
      updatesWithSelection: true,
    });
    card.goalValue = Kpi({
      app,
      objectId: card.nativeObject.goalValueId,
      updatesWithSelection: true,
    });
    card.remainingValue = Kpi({
      app,
      objectId: card.nativeObject.remainingValueId,
      updatesWithSelection: true,
    });
    card.percentage = Kpi({
      app,
      objectId: card.nativeObject.percentageId,
      updatesWithSelection: true,
    });

    initializeRows(card.nativeObject.rankingTableId, card);

    const num = parseInt(card.percentage?.slice(0, -1)) ?? 0;
    card.percentageNum = num > 100 ? 100 : num;
    card.percentageColor = getColorFromPercentage(card.percentageNum);
  }

  const fetchKPIValueShowInsights = useCallback(
    async (objectId) => {
      if (app && objectId) {
        let id = objectId;
        await app.visualization.get(id).then((res) => {
          const value =
            res?.model?.layout?.qHyperCube?.qGrandTotalRow[0]?.qText ??
            res?.model?.layout?.qHyperCube?.qGrandTotalRow[0]?.qNum ??
            null;
          setIngsights(value > 0 ? true : false);
        });
      }
    },
    [app]
  );

  const fetchKPIValue = useCallback(
    async (objectId) => {
      if (app && objectId) {
        let id = objectId;
        await app.visualization.get(id).then((res) => {
          const value =
            res?.model?.layout?.qHyperCube?.qGrandTotalRow[0]?.qText ??
            res?.model?.layout?.qHyperCube?.qGrandTotalRow[0]?.qNum ??
            null;
          setTextQuarter(value);
        });
      }
    },
    [app]
  );

  useEffect(() => {
    const found = buttonGroup?.find((element) => element?.selected);
    if (found) {
      app.field("Visao").selectMatch(found.value);
      fetchKPIValueShowInsights("jEeze");
      fetchKPIValue("jzfBCy");
    }
  }, [app, buttonGroup]);

  return (
    <div className="app sellOutGoldenStores">
      <div className="appContent">
        <LayoutWrapper>
          <Grid
            container
            flexDirection={"column"}
            justifyContent={"space-between"}
            alignItems={"start"}
            rowGap={2}
          >
            {/* TITLE + VIEW TOGGLE */}
            <Grid
              item
              container
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Grid
                item
                display={"flex"}
                alignItems={"center"}
                gap={"10px"}
                className="titles-mobile"
              >
                <Box component={"h1"}>Golden Stores | Sell out</Box>

                {(!openReports || openScreenGroup[2].selected) && (
                  <>
                    <span className="titles-mobile-hifen">-</span>
                    <Grid
                      className="container-periodo"
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                      xs
                      item
                    >
                      <p>Período Analisado:</p>

                      <p
                        style={{
                          fontWeight: "bold",
                          fontSize: "17px",
                          color: "black",
                        }}
                      >
                        {textQuarter}
                      </p>
                    </Grid>
                  </>
                )}
              </Grid>

              <Grid item>
                <ToggleButtonGroup
                  color="primary"
                  value={
                    openScreenGroup[0].selected
                      ? "Overview"
                      : openScreenGroup[1].selected
                      ? "Relatórios"
                      : "Insights"
                  }
                  exclusive
                  onChange={(e, value) => {
                    if (value) {
                      setOpenReports(value === "Relatórios");
                      setOpenScreenGroup((prevState) =>
                        prevState.map((item) =>
                          item.value === value
                            ? { ...item, selected: true }
                            : { ...item, selected: false }
                        )
                      );
                    }
                  }}
                  aria-label="Platform"
                >
                  <ToggleButton
                    size="small"
                    value="Overview"
                    className={`toggleButton`}
                  >
                    Overview
                  </ToggleButton>

                  <ToggleButton
                    size="small"
                    className={`toggleButton`}
                    value="Relatórios"
                  >
                    Relatórios
                  </ToggleButton>
                  {showIngsights && (
                    <ToggleButton
                      size="small"
                      className={`toggleButton`}
                      value="Insights"
                    >
                      Insights
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              </Grid>
            </Grid>
            {!openScreenGroup[2].selected && (
              <Grid item xs sx={{ width: "100%" }}>
                <Card
                  app={app}
                  sx={{
                    minHeight: notFullHd ? "75vh" : "80vh",
                  }}
                >
                  <Grid
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="flex-start"
                    sx={{
                      height: "100%",
                    }}
                  >
                    {/* CARD HEADER */}
                    <Grid
                      container
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      {/* COMBO HEADER + TOGGLE TABLE */}
                      <Grid item>
                        <Grid
                          item
                          container
                          flexDirection="row"
                          justifyContent="flex-start"
                          alignItems="flex-start"
                          xs
                        >
                          <ComboHeader
                            reference={{
                              title: {
                                hard: openReports ? "Relatórios" : "Sell out",
                              },
                            }}
                          />
                          {buttonGroup && openReports && (
                            <ToggleButtonGroup
                              size={"small"}
                              color="primary"
                              value={
                                buttonGroup.find((button) => button.selected)
                                  ?.value ?? buttonGroup[0].value
                              }
                              exclusive
                              onChange={(e, value) => {
                                if (value) {
                                  const group = buttonGroup.map((button) => {
                                    button.selected = button.value === value;
                                    return button;
                                  });
                                  setButtonGroup(group);
                                }
                              }}
                              aria-label="TableOptions"
                              className="toggleButtonGroup"
                            >
                              {buttonGroup.map((button) => (
                                <ToggleButton
                                  key={button.value}
                                  value={button.value}
                                  sx={{
                                    width: "100px",
                                    fontSize: "12px",
                                  }}
                                >
                                  {button.value}
                                </ToggleButton>
                              ))}
                            </ToggleButtonGroup>
                          )}
                        </Grid>
                      </Grid>

                      {/* EXPORT BUTTON */}
                      <Grid item>
                        {openReports && (
                          <Grid
                            item
                            container
                            flexDirection="row"
                            justifyContent="flex-end"
                            alignItems="flex-start"
                            xs
                          >
                            <Tooltip title="Exportar para Excel">
                              <Button
                                type="button"
                                variant="outlined"
                                onClick={() =>
                                  exportToXLS(
                                    "d1ab25ca-0846-4a68-8ffe-b8265ac5eb35",
                                    true
                                  )
                                }
                                endIcon={<FileDownloadOutlinedIcon />}
                                className="export-button"
                              >
                                {!loadingExcel ? (
                                  <Box component={"span"}>Exportar</Box>
                                ) : (
                                  <LoaderWrapper>
                                    <CircleLoader />
                                  </LoaderWrapper>
                                )}
                              </Button>
                            </Tooltip>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>

                    {/* CARD BODY */}
                    {openReports ? (
                      <Grid
                        item
                        sx={{
                          width: "100%",
                          height: "100%",
                          minHeight: notFullHd ? "71vh" : "76vh",
                        }}
                        xs
                      >
                        <NativeObject
                          app={app}
                          key="d1ab25ca-0846-4a68-8ffe-b8265ac5eb35"
                          type={"table"}
                          reference={{
                            id: "d1ab25ca-0846-4a68-8ffe-b8265ac5eb35",
                          }}
                          style={{
                            padding: "0 12px ",
                          }}
                        />
                      </Grid>
                    ) : (
                      <Grid
                        container
                        item
                        flexDirection={"row"}
                        justifyContent={"flex-start"}
                        xs
                        columnGap={1}
                      >
                        {configData?.cards.map((card, index) => (
                          <Grid
                            key={index}
                            container
                            item
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            xs
                          >
                            <Grid
                              className={`sell-out-wrapper bordered`}
                              container
                              item
                              flexDirection={"column"}
                              justifyContent={"flex-start"}
                              rowGap={4}
                              sx={{
                                height: "100%",
                              }}
                              xs
                            >
                              {/* TITLE */}
                              <Grid
                                container
                                item
                                flexDirection={"row"}
                                justifyContent={"flex-start"}
                                alignItems={"center"}
                              >
                                <Box className="kpi-title" component={"span"}>
                                  {card.title.hardTitle}
                                </Box>
                              </Grid>
                              {/* KPI PROGRESS BAR  */}
                              <Grid
                                container
                                flexDirection={"row"}
                                justifyContent={"space-between"}
                                alignItems={"flex-start"}
                                columnGap={4}
                              >
                                {/* KPI */}
                                <Grid
                                  container
                                  item
                                  xs={12}
                                  flexDirection={"column"}
                                  justifyContent={"flex-start"}
                                  alignItems={"flex-start"}
                                  rowGap={1}
                                >
                                  <Grid
                                    className={`value-wrapper`}
                                    container
                                    item
                                    xs={12}
                                    flexDirection={"column"}
                                    justifyContent={"flex-start"}
                                    alignItems={"flex-start"}
                                  >
                                    {/* VALUE */}
                                    <Grid
                                      container
                                      item
                                      flexDirection={"row"}
                                      justifyContent={"flex-start"}
                                      alignItems={"flex-end"}
                                      style={{
                                        width: "100%",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      <Box
                                        component={"span"}
                                        className="value"
                                        style={{
                                          color: card.percentageColor,
                                        }}
                                      >
                                        {card.value}
                                      </Box>
                                    </Grid>

                                    {/* PROGRESS BAR */}
                                    <Grid
                                      container
                                      flexDirection={"column"}
                                      justifyContent={"flex-start"}
                                      alignItems={"flex-start"}
                                      style={{
                                        width: "100%",
                                      }}
                                    >
                                      <ProgressBar
                                        key={card.title.hardTitle}
                                        bgColor={card.percentageColor}
                                        completed={card.percentageNum}
                                      />
                                    </Grid>
                                  </Grid>
                                  {/* GOAL AND PERCENTAGE */}
                                  <Grid
                                    container
                                    item
                                    flexDirection={"row"}
                                    justifyContent={"flex-start"}
                                    alignItems={"flex-start"}
                                    xs
                                  >
                                    {/* GOAL VALUE */}
                                    <Grid
                                      className={`goal-wrapper`}
                                      container
                                      item
                                      flexDirection={"row"}
                                      justifyContent={"flex-start"}
                                      alignItems={"center"}
                                      columnGap={notFullHd ? 0.3 : 0.7}
                                      xs
                                    >
                                      <Grid item>
                                        {/* VALOR EM CINZA */}
                                        <Box
                                          component={"span"}
                                          className="goal-value"
                                        >
                                          {card.goalValue}
                                        </Box>
                                      </Grid>
                                      <Grid item>
                                        <Box
                                          component={"span"}
                                          className="goal-text"
                                        >
                                          Meta
                                        </Box>
                                      </Grid>
                                    </Grid>

                                    {/* GOAL PERCENTAGE */}
                                    <Grid
                                      className={`percentage-wrapper`}
                                      container
                                      item
                                      flexDirection={"row"}
                                      justifyContent={"flex-start"}
                                      alignItems={"center"}
                                      columnGap={notFullHd ? 0 : 0.7}
                                      xs={4.5}
                                    >
                                      <Grid item>
                                        <Box
                                          component={"span"}
                                          className="percentage-value"
                                          style={{
                                            color: card?.percentageColor,
                                          }}
                                        >
                                          {card?.percentage}
                                        </Box>
                                      </Grid>
                                      <Grid item>
                                        <Box
                                          component={"span"}
                                          className="percentage-text"
                                        >
                                          Ating.
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>

                              {/* RANKING */}
                              <Grid
                                container
                                flexDirection={"column"}
                                justifyContent={"space-between"}
                              >
                                <Grid item xs>
                                  <ComboHeader
                                    style={{
                                      fontSize: "0.5rem",
                                    }}
                                    reference={{
                                      title: {
                                        hard: `Ranking ${
                                          index === 0 ? "Geral" : ""
                                        }`,
                                      },
                                    }}
                                    scale
                                    innerTitle
                                    legends={[
                                      {
                                        type: "bar",
                                        iconColor: "#F98561",
                                        name: "Meta",
                                      },
                                      {
                                        type: "bar",
                                        iconColor: "#55B6EB",
                                        name: "Realizado",
                                      },
                                    ]}
                                  />
                                </Grid>
                                {loading || !card?.rows ? (
                                  <ObjectLoader
                                    icon="table"
                                    height="inherit"
                                    width="inherit"
                                  />
                                ) : (
                                  <Grid
                                    container
                                    flexDirection={"row"}
                                    justifyContent={"center"}
                                    alignItems={"flex-start"}
                                    rowGap={1}
                                  >
                                    <Grid
                                      container
                                      item
                                      flexDirection={"row"}
                                      justifyContent={"flex-start"}
                                      sx={{
                                        maxHeight: notFullHd ? "38vh" : "50vh",
                                        overflowY: "scroll",
                                      }}
                                      xs={12}
                                    >
                                      <Grid
                                        container
                                        item
                                        flexDirection={"column"}
                                        justifyContent={"center"}
                                        rowGap={2}
                                        alignItems={"center"}
                                        sx={{
                                          height: "100%",
                                          margin: "0 8px 0 4px",
                                        }}
                                        xs={12}
                                      >
                                        {card?.rows?.length
                                          ? card.rows?.map((row, index) => (
                                              <Grid
                                                key={index}
                                                container
                                                item
                                                flexDirection={"column"}
                                                justifyContent={"center"}
                                                alignItems={"flex-start"}
                                                xs={12}
                                                rowGap={0.8}
                                              >
                                                {/* Distributor title and score */}
                                                <Grid item xs>
                                                  <Box
                                                    className="score"
                                                    component={"span"}
                                                  >
                                                    {`${row.scoreValue} pontos | ${row.name}`}
                                                  </Box>
                                                </Grid>
                                                {/* Distributor goal and reached value */}
                                                <Grid
                                                  item
                                                  container
                                                  flexDirection={"column"}
                                                  justifyContent={"center"}
                                                  alignItems={"flex-start"}
                                                  xs={12}
                                                >
                                                  <Grid
                                                    item
                                                    sx={{
                                                      width: "100%",
                                                    }}
                                                  >
                                                    <Bar
                                                      tooltip={`R$ ${row.goalValue.toLocaleString(
                                                        "pt-BR"
                                                      )}`}
                                                      color={"#F98561"}
                                                      completed={
                                                        (row.goalValue /
                                                          card.highestValue) *
                                                        100
                                                      }
                                                    ></Bar>
                                                  </Grid>
                                                  <Grid
                                                    item
                                                    sx={{
                                                      width: "100%",
                                                    }}
                                                  >
                                                    <Bar
                                                      tooltip={`R$ ${row.value.toLocaleString(
                                                        "pt-BR"
                                                      )}`}
                                                      color={"#55B6EB"}
                                                      completed={
                                                        (row.value /
                                                          card.highestValue) *
                                                        100
                                                      }
                                                    ></Bar>
                                                  </Grid>
                                                </Grid>
                                              </Grid>
                                            ))
                                          : null}
                                      </Grid>
                                    </Grid>
                                    {card?.scale?.length && (
                                      <Grid
                                        item
                                        container
                                        flexDirection={"column"}
                                        justifyContent={"center"}
                                        rowGap={0.5}
                                        xs
                                      >
                                        <Divider />

                                        <Grid
                                          container
                                          flexDirection={"row"}
                                          justifyContent={"space-between"}
                                          alignItems={"center"}
                                        >
                                          {card?.scale?.map((item, index) => (
                                            <Grid
                                              key={index}
                                              item
                                              container
                                              xs
                                              flexDirection={"row"}
                                              justifyContent={
                                                index === 2
                                                  ? "flex-end"
                                                  : "flex-start"
                                              }
                                            >
                                              <Box
                                                component={"span"}
                                                sx={{
                                                  fontWeight: "400",
                                                  color: "#D3D3D3",
                                                }}
                                              >
                                                {item.toLocaleString("pt-BR")}
                                              </Box>
                                            </Grid>
                                          ))}
                                        </Grid>
                                      </Grid>
                                    )}
                                  </Grid>
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                </Card>
              </Grid>
            )}

            {openScreenGroup[2].selected && (
              <InsightsGoldenDesktop app={app} config={config} />
            )}
          </Grid>
        </LayoutWrapper>
      </div>
    </div>
  );
}

SellOutGoldenStoresDesktop.defaultProps = {
  app: null,
  config: {},
};

SellOutGoldenStoresDesktop.propTypes = {
  app: PropTypes.object,
  config: PropTypes.object,
};

export default SellOutGoldenStoresDesktop;
